import { call, put, fork, takeEvery, select, race, take } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { normalize } from 'normalizr';
import { push } from 'react-router-redux';
import { show, hide } from 'redux-modal';
import moment from 'moment';
import { startSubmit, stopSubmit } from 'redux-form';

import * as schemas from 'store/common/schemas';
import * as actionTypes from './action-types';
import * as actions from './actions';
import * as entityActions from 'store/entities/actions';
import * as authActions from 'store/features/auth/actions';
import { selectUserTeamMemberships, selectTeamMembership } from './selectors';
import callApi from 'store/api/saga';
import * as api from 'api/config/users';
import {
  selectCurrentUser,
  selectCurrentUserId,
  selectCurrentUserEthereumAddress,
} from 'store/features/auth/selectors';
import routeGenerators from 'ui/common/routes/generators';
import { MODAL_NAME as SET_ETHEREUM_ADDRESS_MODAL_NAME } from 'ui/users/set-eth-address/index';

import { SET_ETHEREUM_ADDRESS } from 'store/features/users/action-types';

import { connectionActions as connectionActionsMessages } from 'ui/users/team-memberships/messages';
import { confirmSaga as confirm } from 'store/features/common/confirmation-dialog/saga';
import { addMessage } from 'store/features/common/flash/actions';
import { messageTypes } from 'store/features/common/flash/builder';
import { TEAM_MEMBER_CONNECTION_STATUSES } from 'store/features/company-team-member-connections/helpers';

const MODAL_HIDE = '@redux-modal/HIDE';

const MAX_POLL_TIME_IN_SECONDS =
  Number(process.env.REACT_APP_MAX_CONNECTIONS_POLL_TIME_IN_SECONDS) || 60;
const POLL_INTERVAL_IN_SECONDS =
  Number(process.env.REACT_APP_CONNECTIONS_POLL_INTERVAL_IN_SECONDS) || 10;

function* pollTeamMembershipStatus({
  prettyId,
  teamMembershipId,
  timeoutStartsAt,
  startStatuses = [],
  stopStatuses = [],
}) {
  if (!prettyId || !teamMembershipId || !timeoutStartsAt) return false;

  let teamMembership = yield select(selectTeamMembership, { teamMembershipId });
  if (!teamMembership || startStatuses.indexOf(teamMembership.status) === -1) return false;

  const stopAt = moment(timeoutStartsAt).add(MAX_POLL_TIME_IN_SECONDS, 'seconds');
  yield put(actions.pollCurrentUserTeamMembership.start({ prettyId, teamMembershipId }));
  while (moment() < stopAt) {
    yield call(fetchTeamMembership, { payload: { prettyId, teamMembershipId } });
    teamMembership = yield select(selectTeamMembership, { teamMembershipId });

    if (stopStatuses.indexOf(teamMembership.status) >= 0) {
      yield put(actions.pollCurrentUserTeamMembership.stop({ prettyId, teamMembershipId }));
      return true;
    }

    yield delay(POLL_INTERVAL_IN_SECONDS * 1000);
  }

  yield put(actions.pollCurrentUserTeamMembership.stop({ prettyId, teamMembershipId }));
  return false;
}

function* checkCompanySideTransaction({ team: teamMembership }) {
  if (teamMembership.id && teamMembership.company && teamMembership.company.prettyId) {
    if (
      teamMembership.status === TEAM_MEMBER_CONNECTION_STATUSES.pendingTransactionSigningByCompany
    ) {
      try {
        yield call(
          callApi,
          api.acceptTeamMembership({
            companyPrettyId: teamMembership.company.prettyId,
            teamMembershipId: teamMembership.id,
          })
        );
      } catch (error) {
        console.error(error);
      }
    }

    yield fork(pollTeamMembershipStatus, {
      teamMembershipId: teamMembership.id,
      prettyId: teamMembership.company.prettyId,
      timeoutStartsAt: teamMembership.acceptedTimestamp,
      startStatuses: [TEAM_MEMBER_CONNECTION_STATUSES.pendingTransactionSigningByCompany],
      stopStatuses: [TEAM_MEMBER_CONNECTION_STATUSES.signedByBoth],
    });
  }
}

function* fetchUserTeamMemberships({ payload }) {
  const { userId } = payload;
  const currentUserId = yield select(selectCurrentUserId);
  yield put(actions.fetchUserTeamMemberships.start({ userId }));

  try {
    let response = yield call(
      callApi,
      currentUserId === userId
        ? api.fetchCurrentUserTeamMemberships()
        : api.fetchUserTeamMemberships({ userId }) // TODO: public view api need to be updated
    );
    /*[
      {
        teamMembershipId: 'anotherthing',
        id: 'something',
        signed_by_user: false,
        signed_by_company: true,
        company: { id: 'id', company_name: 'Test', prettyId: 'IndorseTest' },
        rejected: false,
        designation: 'designation',
      },
    ];
*/

    response = response.map(item => {
      item.user = { id: userId };
      return item;
    });

    const schema = [schemas.companyTeamMemberConnection];
    const { entities, result } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));

    yield put(actions.fetchUserTeamMemberships.success({ userId, teams: result }));

    if (userId === currentUserId) {
      const teams = yield select(selectUserTeamMemberships, { userId });
      for (let teamIndex = 0; teamIndex < teams.length; teamIndex++) {
        const team = teams[teamIndex];
        yield fork(checkCompanySideTransaction, { team });
      }
    }
  } catch (error) {
    yield put(actions.fetchUserTeamMemberships.failure({ userId, error: error.message }));
  }
}

function* watchFetchUserTeamMemberships() {
  yield takeEvery(actionTypes.FETCH_TEAM_MEMBERSHIPS_BY_USER_ID.REQUEST, fetchUserTeamMemberships);
}

function* fetchTeamMembership({ payload }) {
  const { prettyId, teamMembershipId } = payload;
  yield put(actions.fetchTeamMembership.start({ prettyId, teamMembershipId }));

  try {
    const response = yield call(callApi, api.fetchTeamMembership({ prettyId, teamMembershipId }));
    response.company = { prettyId };

    const schema = schemas.companyTeamMemberConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));

    yield put(actions.fetchTeamMembership.success({ prettyId, teamMembershipId }));
  } catch (error) {
    yield put(
      actions.fetchTeamMembership.failure({ prettyId, teamMembershipId, error: error.message })
    );
  }
}

function* watchFetchTeamMembership() {
  yield takeEvery(actionTypes.FETCH_TEAM_MEMBERSHIP.REQUEST, fetchTeamMembership);
}

function* checkAcceptTeamMembershipStatus({ payload }) {
  const { prettyId, teamMembershipId } = payload;
  const currentUserId = yield select(selectCurrentUserId);
  const currentUserEthereumAddress = yield select(selectCurrentUserEthereumAddress);

  yield put(actions.checkAcceptTeamMembershipStatus.start({ prettyId, teamMembershipId }));
  try {
    if (currentUserId && !currentUserEthereumAddress) {
      yield put(show(SET_ETHEREUM_ADDRESS_MODAL_NAME));
      const { ethAddressSet } = yield race({
        ethAddressSet: take(
          action =>
            action &&
            action.type === SET_ETHEREUM_ADDRESS.SUCCESS &&
            action.payload &&
            action.payload.userId === currentUserId
        ),
        ethAddressModalClosed: take(
          action =>
            action &&
            action.type === MODAL_HIDE &&
            action.payload &&
            action.payload.modal === SET_ETHEREUM_ADDRESS_MODAL_NAME
        ),
      });
      // Safety net
      yield put(hide(SET_ETHEREUM_ADDRESS_MODAL_NAME));
      if (!ethAddressSet) {
        yield put(
          actions.checkAcceptTeamMembershipStatus.failure({
            error: 'Eth address not set',
            prettyId,
            teamMembershipId,
          })
        );
        return;
      }
    }

    const response = yield call(
      callApi,
      api.acceptTeamMembership({ companyPrettyId: prettyId, teamMembershipId })
    );

    const responseStatus = response && response.statusCode;
    const responseMessage = response && response.message;

    yield put(authActions.setHasConnectionsEntity(responseStatus && responseStatus > 1));

    yield put(actions.checkAcceptTeamMembershipStatus.success({ prettyId, teamMembershipId }));

    if (responseStatus === 2) throw new Error(responseMessage);
    if (responseStatus === 5) {
      yield call(checkAcceptTeamMembershipStatus, { payload: { teamMembershipId } });
      yield put(addMessage({ kind: messageTypes.success, content: responseMessage }));
    } else {
      yield put(push(routeGenerators.companies.acceptAsTeamMember({ prettyId, teamMembershipId })));
    }
  } catch (error) {
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
    yield put(
      actions.checkAcceptTeamMembershipStatus.failure({
        error: error.message,
        prettyId,
        teamMembershipId,
      })
    );
  }
}

function* watchCheckAcceptTeamMembershipStatus() {
  yield takeEvery(
    actionTypes.CHECK_ACCEPT_TEAM_MEMBERSHIP_STATUS.REQUEST,
    checkAcceptTeamMembershipStatus
  );
}

function* watchShowAcceptTeamMembershipInstructions() {
  yield takeEvery(
    actionTypes.SHOW_ACCEPT_TEAM_MEMBERSHIP_INSTRUCTIONS,
    checkAcceptTeamMembershipStatus
  );
}

function* completeAcceptTeamMembership({ prettyId, teamMembershipId, txHash }) {
  try {
    yield call(fetchTeamMembership, { payload: { prettyId, teamMembershipId } });
    const teamMembership = yield select(selectTeamMembership, { teamMembershipId });

    yield call(pollTeamMembershipStatus, {
      prettyId,
      teamMembershipId,
      timeoutStartsAt: teamMembership.acceptedTimestamp || teamMembership.creationTimestamp,
      startStatuses: [
        TEAM_MEMBER_CONNECTION_STATUSES.pendingTransactionSigningByBoth,
        TEAM_MEMBER_CONNECTION_STATUSES.pendingTransactionSigningByCompany,
        TEAM_MEMBER_CONNECTION_STATUSES.pendingTransactionSigningByUser,
      ],
      stopStatuses: [TEAM_MEMBER_CONNECTION_STATUSES.signedByBoth],
    });

    yield call(
      callApi,
      api.acceptTeamMembership({ companyPrettyId: prettyId, teamMembershipId, txHash })
    );
  } catch (error) {
    yield put(
      actions.acceptTeamMembership.failure({ error: error.message, teamMembershipId, txHash })
    );
    yield put(
      addMessage({
        kind: messageTypes.danger,
        content: error.message,
      })
    );
  }
}

function* acceptTeamMembership({ payload, meta = {} }) {
  let { teamMembershipId, txHash } = payload;

  let teamMembership = yield select(selectTeamMembership, { teamMembershipId });

  const { form } = meta;
  const { company: { prettyId } } = teamMembership;

  yield put(actions.acceptTeamMembership.start({ teamMembershipId, txHash }));
  if (form) yield put(startSubmit(form));

  try {
    yield call(
      callApi,
      api.acceptTeamMembership({ companyPrettyId: prettyId, teamMembershipId, txHash })
    );

    yield call(fetchTeamMembership, { payload: { prettyId, teamMembershipId } });
    teamMembership = yield select(selectTeamMembership, { teamMembershipId });

    yield put(actions.acceptTeamMembership.success({ teamMembershipId, txHash }));
    if (form) yield put(stopSubmit(form));
  } catch (error) {
    yield put(
      actions.acceptTeamMembership.failure({ error: error.message, teamMembershipId, txHash })
    );
    if (form) yield put(stopSubmit(form, error.formErrors));
  } finally {
    const currentUser = yield select(selectCurrentUser);
    const profileUrl = routeGenerators.users.bestProfile({ user: currentUser });
    yield put(push(profileUrl));

    yield call(completeAcceptTeamMembership, { prettyId, teamMembershipId, txHash });
  }
}

function* watchAcceptTeamMembership() {
  yield takeEvery(actionTypes.ACCEPT_TEAM_MEMBERSHIP.REQUEST, acceptTeamMembership);
}

//#region "Reject"
function* rejectTeamMembership({ payload }) {
  const { teamMembershipId } = payload;
  const teamMembership = yield select(selectTeamMembership, { teamMembershipId });
  const { company } = teamMembership;

  const confirmationMessage = {
    ...connectionActionsMessages.confirmReject,
    values: { companyName: company.companyName },
  };
  const confirmed = yield call(confirm, { message: confirmationMessage });
  if (!confirmed) {
    return;
  }

  yield put(actions.rejectTeamMembership.start({ teamMembershipId }));

  try {
    yield call(
      callApi,
      api.rejectTeamMembership({ companyPrettyId: company.prettyId, teamMembershipId })
    );
    // Ideally api should return modified team-memberships. But for now, we would mark rejected as true here
    yield put(actions.rejectTeamMembership.success({ teamMembershipId }));
    yield put(
      addMessage({
        kind: messageTypes.success,
        content: {
          ...connectionActionsMessages.rejected,
          values: { companyName: company.companyName },
        },
      })
    );
  } catch (error) {
    yield put(actions.rejectTeamMembership.failure({ error: error.message, team: teamMembership }));
  }
}

function* watchRejectTeamMembership() {
  yield takeEvery(actionTypes.REJECT_TEAM_MEMBERSHIP.REQUEST, rejectTeamMembership);
}

//#endregion

//#region Revoke
function* showRevokeTeamMembershipInstructions({ payload }) {
  const { teamMembershipId } = payload;
  const teamMembership = yield select(selectTeamMembership, { teamMembershipId });
  const { company: { prettyId } } = teamMembership;

  const confirmationMessage = {
    ...connectionActionsMessages.confirmRevoke,
    values: { companyName: prettyId },
  };
  const confirmed = yield call(confirm, { message: confirmationMessage });
  if (!confirmed) {
    return;
  }

  // check the status
  if (
    teamMembership.status === TEAM_MEMBER_CONNECTION_STATUSES.signedByBoth ||
    teamMembership.status === TEAM_MEMBER_CONNECTION_STATUSES.pendingTransactionSigningByCompany
  ) {
    yield put(
      push(
        routeGenerators.companies.revokeAsTeamMember({
          prettyId,
          teamMembershipId: teamMembership.id,
        })
      )
    );
  } else {
    // TODO: show msg
    return;
  }
}

function* watchShowRevokeTeamMembershipInstructions() {
  yield takeEvery(
    actionTypes.SHOW_REVOKE_TEAM_MEMBERSHIP_INSTRUCTIONS,
    showRevokeTeamMembershipInstructions
  );
}

function* revokeTeamMembership({ payload, meta = {} }) {
  let { teamMembershipId, txHash } = payload;
  const teamMembership = yield select(selectTeamMembership, { teamMembershipId });
  const { form } = meta;
  const { company: { prettyId } } = teamMembership;
  const currentUser = yield select(selectCurrentUser);

  yield put(actions.revokeTeamMembership.start({ teamMembershipId, txHash }));

  if (form) yield put(startSubmit(form));
  try {
    const profileUrl = routeGenerators.users.bestProfile({ user: currentUser });
    yield put(push(profileUrl));

    const revoked = yield call(pollTeamMembershipStatus, {
      prettyId,
      teamMembershipId,
      timeoutStartsAt: moment(),
      startStatuses: [TEAM_MEMBER_CONNECTION_STATUSES.signedByBoth],
      stopStatuses: [TEAM_MEMBER_CONNECTION_STATUSES.pendingTransactionSigningByUser],
    });

    if (revoked) {
      yield call(
        callApi,
        api.revokeTeamMembership({
          companyPrettyId: prettyId,
          teamMembershipId: teamMembershipId,
          txHash: txHash,
        })
      );

      const schema = schemas.companyTeamMemberConnection;
      yield put(entityActions.removeEntities({ [schema.key]: [teamMembershipId] }));
      yield put(actions.revokeTeamMembership.success({ teamMembershipId, txHash }));
      if (form) yield put(stopSubmit(form));

      yield put(
        addMessage({
          kind: messageTypes.success,
          content: {
            ...connectionActionsMessages.revoked,
            values: { companyName: prettyId },
          },
        })
      );
    } else {
      if (form) yield put(stopSubmit(form));
      // TODO: Decide to error message or not
      yield put(
        actions.revokeTeamMembership.failure({
          error: 'Transaction is not mined',
          teamMembershipId,
          txHash,
        })
      );
    }
  } catch (error) {
    yield put(
      actions.revokeTeamMembership.failure({ error: error.message, teamMembershipId, txHash })
    );
  }
}

function* watchRevokeTeamMembership() {
  yield takeEvery(actionTypes.REVOKE_TEAM_MEMBERSHIP.REQUEST, revokeTeamMembership);
}
//#endregion

export default function* teamMemberships() {
  yield fork(watchFetchUserTeamMemberships);
  yield fork(watchCheckAcceptTeamMembershipStatus);
  yield fork(watchShowAcceptTeamMembershipInstructions);
  yield fork(watchAcceptTeamMembership);
  yield fork(watchRejectTeamMembership);
  yield fork(watchRevokeTeamMembership);
  yield fork(watchShowRevokeTeamMembershipInstructions);
  yield fork(watchFetchTeamMembership);
}



// WEBPACK FOOTER //
// ./src/store/features/users/team-memberships/saga.js